import { useCallback, useEffect, useMemo, useState } from "react";
import { api, API, getStoredToken, formatApiError, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import confetti from "canvas-confetti";
import {
  GraduationCap,
  PlayCircle,
  FileText,
  BookOpen,
  ExternalLink,
  Plus,
  Pencil,
  Trash2,
  X,
  EyeOff,
  CheckCircle2,
  Circle,
  Users,
  Download,
} from "lucide-react";

// Detect a known embeddable video host and return the iframe `src` to embed.
// Returns null if the URL isn't a recognized YouTube/Loom/Vimeo link.
function getEmbedSrc(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    const host = u.hostname.replace(/^www\./, "");
    // YouTube
    if (host === "youtu.be") {
      const id = u.pathname.slice(1).split("/")[0];
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host === "youtube.com" || host === "m.youtube.com") {
      if (u.pathname === "/watch") {
        const id = u.searchParams.get("v");
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
      if (u.pathname.startsWith("/embed/")) return url;
      if (u.pathname.startsWith("/shorts/")) {
        const id = u.pathname.split("/")[2];
        if (id) return `https://www.youtube.com/embed/${id}`;
      }
    }
    // Loom
    if (host === "loom.com" || host.endsWith(".loom.com")) {
      if (u.pathname.startsWith("/share/")) {
        const id = u.pathname.split("/")[2];
        if (id) return `https://www.loom.com/embed/${id}`;
      }
      if (u.pathname.startsWith("/embed/")) return url;
    }
    // Vimeo
    if (host === "vimeo.com") {
      const id = u.pathname.split("/").filter(Boolean)[0];
      if (id && /^\d+$/.test(id)) return `https://player.vimeo.com/video/${id}`;
    }
    if (host === "player.vimeo.com") return url;
  } catch {
    return null;
  }
  return null;
}

const KINDS = [
  { value: "video", label: "Video", icon: PlayCircle },
  { value: "manual", label: "Manual / PDF", icon: FileText },
  { value: "article", label: "Article / Doc", icon: BookOpen },
  { value: "other", label: "Other", icon: ExternalLink },
];

const CATEGORIES = [
  { value: "getting_started", label: "Getting Started" },
  { value: "anchor_intake", label: "A.N.C.H.O.R. Intake" },
  { value: "care_seekers", label: "Care Seekers" },
  { value: "referrals", label: "Referrals" },
  { value: "appointments", label: "Appointments" },
  { value: "maternal_wellness", label: "Maternal & Family" },
  { value: "outreach", label: "Outreach" },
  { value: "reports", label: "Reports" },
  { value: "team_admin", label: "Team Admin" },
  { value: "other", label: "Other" },
];

const CATEGORY_LABEL = Object.fromEntries(CATEGORIES.map((c) => [c.value, c.label]));
const KIND_META = Object.fromEntries(KINDS.map((k) => [k.value, k]));

// Fire a celebratory burst of confetti from both lower corners of the screen.
function fireCelebration() {
  const duration = 2200;
  const end = Date.now() + duration;
  const colors = ["#1E3A8A", "#166534", "#FCD34D", "#7E937D", "#FFFFFF"];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 70, origin: { x: 0, y: 0.85 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 70, origin: { x: 1, y: 0.85 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // One big center burst for emphasis
  confetti({ particleCount: 90, spread: 110, startVelocity: 35, origin: { y: 0.4 }, colors });
}

export default function Training() {
  const { can } = useAuth();
  const isAdmin = can("training.create");

  const [items, setItems] = useState([]);
  const [completedIds, setCompletedIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [showDrafts, setShowDrafts] = useState(isAdmin);
  const [hideWatched, setHideWatched] = useState(false);
  const [showProgress, setShowProgress] = useState(false);
  const [playing, setPlaying] = useState(null); // {title, embedSrc, url}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [tr, comp] = await Promise.all([
        api.get("/trainings", {
          params: showDrafts && isAdmin ? { include_drafts: true } : {},
        }),
        api.get("/trainings/me/completions").catch(() => ({ data: [] })),
      ]);
      setItems(tr.data || []);
      setCompletedIds(new Set((comp.data || []).map((c) => c.training_id)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [showDrafts, isAdmin]);
  useEffect(() => { load(); }, [load]);

  const grouped = useMemo(() => {
    const map = {};
    for (const t of items) {
      if (hideWatched && completedIds.has(t.id)) continue;
      const k = t.category || "other";
      (map[k] ??= []).push(t);
    }
    // Stable category order from CATEGORIES list, then any unknown ones
    const order = CATEGORIES.map((c) => c.value);
    return order
      .map((k) => [k, map[k] || []])
      .concat(Object.entries(map).filter(([k]) => !order.includes(k)))
      .filter(([, arr]) => arr.length > 0);
  }, [items, hideWatched, completedIds]);

  const publishedCount = useMemo(() => items.filter((t) => t.published).length, [items]);
  const myCompletedCount = useMemo(
    () => items.filter((t) => t.published && completedIds.has(t.id)).length,
    [items, completedIds],
  );

  const toggleWatched = async (t) => {
    const isDone = completedIds.has(t.id);
    // Optimistic update
    setCompletedIds((prev) => {
      const next = new Set(prev);
      if (isDone) next.delete(t.id);
      else next.add(t.id);
      return next;
    });
    try {
      if (isDone) {
        await api.delete(`/trainings/${t.id}/complete`);
      } else {
        const { data } = await api.post(`/trainings/${t.id}/complete`);
        if (data?.just_certified) {
          fireCelebration();
          toast.success("🎉 You're HarborWell Connect Certified! Check your email.", {
            duration: 6000,
          });
        } else {
          toast.success(`Marked "${t.title}" as watched`);
        }
      }
    } catch (e) {
      // Roll back on failure
      setCompletedIds((prev) => {
        const next = new Set(prev);
        if (isDone) next.add(t.id);
        else next.delete(t.id);
        return next;
      });
      toast.error(formatApiError(e));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete "${t.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/trainings/${t.id}`);
      toast.success("Training removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-6" data-testid="training-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <div className="hw-eyebrow mb-2">Learn HarborWell Connect</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold text-[#0F172A]">Training</h1>
          <p className="text-sm text-[#334155] mt-1 max-w-2xl">
            Guides, videos, and reference material for every part of the platform. New team
            members — start with <span className="font-medium">Getting Started</span>.
          </p>
          {publishedCount > 0 && (
            <div
              data-testid="my-progress-strip"
              className={`mt-3 inline-flex items-center gap-3 border rounded-full px-3.5 py-1.5 transition-colors ${
                myCompletedCount >= publishedCount
                  ? "bg-[#F0FDF4] border-[#86EFAC]"
                  : "bg-white border-[#E5E5E0]"
              }`}
            >
              <span className={`text-xs font-medium ${
                myCompletedCount >= publishedCount ? "text-[#166534]" : "text-[#1E3A8A]"
              }`}>
                {myCompletedCount >= publishedCount ? "Certified" : "Your progress"}
              </span>
              <div className="w-32 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                <div
                  className={`h-full transition-all ${
                    myCompletedCount >= publishedCount ? "bg-[#166534]" : "bg-[#1E3A8A]"
                  }`}
                  style={{ width: `${publishedCount ? Math.round((myCompletedCount / publishedCount) * 100) : 0}%` }}
                />
              </div>
              <span className="text-xs text-[#0F172A] font-semibold">
                {myCompletedCount} / {publishedCount}
              </span>
              {myCompletedCount >= publishedCount && (
                <span data-testid="certified-badge" className="text-base" role="img" aria-label="party">🎉</span>
              )}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {publishedCount > 0 && (
            <button
              onClick={() => setHideWatched((h) => !h)}
              data-testid="hide-watched-toggle"
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                hideWatched
                  ? "bg-[#E8ECE7] text-[#166534] border-[#86EFAC]"
                  : "bg-white text-[#334155] border-[#E5E5E0] hover:bg-[#F9F8F6]"
              }`}
            >
              <CheckCircle2 className="w-3 h-3" strokeWidth={2} />
              {hideWatched ? "Hiding watched" : "Hide watched"}
            </button>
          )}
          {isAdmin && (
            <>
              <button
                onClick={() => setShowProgress(true)}
                data-testid="team-progress-btn"
                title="See who on the team has and hasn't completed each training"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium bg-white text-[#1E3A8A] border border-[#E5E5E0] hover:bg-[#F9F8F6]"
              >
                <Users className="w-3 h-3" strokeWidth={2} />
                Team progress
              </button>
              <button
                onClick={() => setShowDrafts((d) => !d)}
                data-testid="toggle-drafts"
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  showDrafts
                    ? "bg-[#F9F8F6] text-[#334155] border-[#E5E5E0]"
                    : "bg-white text-[#1E3A8A] border-[#E5E5E0]"
                }`}
                title="Show unpublished drafts in this list"
              >
                <EyeOff className="w-3 h-3" strokeWidth={2} />
                {showDrafts ? "Showing drafts" : "Hide drafts"}
              </button>
              <button
                onClick={() => { setEditing(null); setShowForm(true); }}
                data-testid="add-training-btn"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554]"
              >
                <Plus className="w-4 h-4" /> Add training
              </button>
            </>
          )}
        </div>
      </div>

      <QuickStartGuideCard />

      {loading ? (
        <div className="text-center py-12 text-[#1E3A8A]">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-12 text-center" data-testid="training-empty">
          <GraduationCap className="w-10 h-10 text-[#1E3A8A] mx-auto mb-3" strokeWidth={1.5} />
          <h3 className="font-display text-lg font-semibold text-[#0F172A]">No training materials yet</h3>
          <p className="text-sm text-[#334155] mt-2 max-w-md mx-auto">
            {isAdmin
              ? "Add your first video, manual, or article so the team can refer back to it."
              : "Check back soon — your admin is putting together training materials."}
          </p>
          {isAdmin && (
            <button
              onClick={() => { setEditing(null); setShowForm(true); }}
              data-testid="empty-add-training"
              className="mt-4 inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium"
            >
              <Plus className="w-4 h-4" /> Add first training
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-8" data-testid="training-sections">
          {grouped.map(([cat, list]) => (
            <section key={cat} data-testid={`training-section-${cat}`}>
              <div className="flex items-baseline gap-2 mb-3">
                <h2 className="font-display text-lg font-semibold text-[#0F172A]">
                  {CATEGORY_LABEL[cat] || cat}
                </h2>
                <span className="text-xs text-[#1E3A8A]">{list.length} item{list.length > 1 ? "s" : ""}</span>
              </div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {list.map((t) => (
                  <TrainingCard
                    key={t.id}
                    item={t}
                    isAdmin={isAdmin}
                    completed={completedIds.has(t.id)}
                    onToggleWatched={() => toggleWatched(t)}
                    onPlay={() => setPlaying({ title: t.title, embedSrc: getEmbedSrc(t.url), url: t.url })}
                    onEdit={() => { setEditing(t); setShowForm(true); }}
                    onDelete={() => remove(t)}
                  />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}

      {showForm && (
        <TrainingForm
          existing={editing}
          onClose={() => { setShowForm(false); setEditing(null); }}
          onSaved={() => { setShowForm(false); setEditing(null); load(); }}
        />
      )}

      {playing && (
        <EmbedModal
          title={playing.title}
          embedSrc={playing.embedSrc}
          url={playing.url}
          onClose={() => setPlaying(null)}
        />
      )}

      {showProgress && (
        <TeamProgressDrawer onClose={() => setShowProgress(false)} />
      )}
    </div>
  );
}

function TrainingCard({ item, isAdmin, completed, onToggleWatched, onPlay, onEdit, onDelete }) {
  const meta = KIND_META[item.kind] || KIND_META.other;
  const Icon = meta.icon;
  const isVideo = item.kind === "video";
  const embedSrc = isVideo ? getEmbedSrc(item.url) : null;
  const canEmbed = !!embedSrc;

  return (
    <div
      data-testid={`training-card-${item.id}`}
      className={`bg-white rounded-2xl border p-4 hw-card-hover relative transition-colors ${
        !item.published
          ? "border-[#FCD34D] bg-[#FFFBEB]"
          : completed
          ? "border-[#86EFAC] bg-[#F0FDF4]"
          : "border-[#E5E5E0]"
      }`}
    >
      <div className="flex items-start gap-3">
        <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
          isVideo ? "bg-[#FEE2E2] text-[#991B1B]" :
          item.kind === "manual" ? "bg-[#DBEAFE] text-[#1E40AF]" :
          "bg-[#E8ECE7] text-[#1E3A8A]"
        }`}>
          <Icon className="w-5 h-5" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A] font-semibold">
              {meta.label}
            </span>
            {!item.published && (
              <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#FEF3C7] text-[#92400E] font-semibold">
                Draft
              </span>
            )}
            {completed && (
              <span
                data-testid={`watched-badge-${item.id}`}
                className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-[#E8ECE7] text-[#166534] font-semibold inline-flex items-center gap-1"
              >
                <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Watched
              </span>
            )}
          </div>
          <h3 className="font-medium text-[#0F172A] mt-0.5 leading-snug">{item.title}</h3>
          {item.description && (
            <p className="text-xs text-[#334155] mt-1 line-clamp-3">{item.description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F0EFEB] gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          {item.url ? (
            canEmbed ? (
              <button
                type="button"
                onClick={onPlay}
                data-testid={`training-play-${item.id}`}
                className="text-xs text-[#1E3A8A] font-medium inline-flex items-center gap-1 hover:underline"
              >
                <PlayCircle className="w-3.5 h-3.5" strokeWidth={1.75} /> Watch here
              </button>
            ) : (
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                data-testid={`training-open-${item.id}`}
                className="text-xs text-[#1E3A8A] font-medium inline-flex items-center gap-1 hover:underline"
              >
                {isVideo ? "Watch" : "Open"} <ExternalLink className="w-3 h-3" strokeWidth={1.75} />
              </a>
            )
          ) : (
            <span className="text-xs text-[#1E3A8A]">No link yet</span>
          )}
          {item.published && (
            <button
              type="button"
              onClick={onToggleWatched}
              data-testid={`toggle-watched-${item.id}`}
              className={`text-[11px] font-medium inline-flex items-center gap-1 px-2.5 py-1 rounded-full transition-colors ${
                completed
                  ? "bg-[#E8ECE7] text-[#166534] hover:bg-[#D1F2D9]"
                  : "bg-white border border-[#E5E5E0] text-[#1E3A8A] hover:bg-[#F9F8F6]"
              }`}
            >
              {completed
                ? <><CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Completed</>
                : <><Circle className="w-3 h-3" strokeWidth={2} /> Mark watched</>}
            </button>
          )}
        </div>
        {isAdmin && (
          <div className="flex items-center gap-1">
            <button
              onClick={onEdit}
              data-testid={`edit-training-${item.id}`}
              className="text-[11px] text-[#1E3A8A] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#F9F8F6]"
            >
              <Pencil className="w-3 h-3" /> Edit
            </button>
            <button
              onClick={onDelete}
              data-testid={`delete-training-${item.id}`}
              className="text-[11px] text-[#991B1B] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#FEE2E2]"
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function TeamProgressDrawer({ onClose }) {
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null); // user_id

  useEffect(() => {
    api
      .get("/trainings/progress")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatApiError(e)));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end bg-[#0F172A]/40"
      onClick={onClose}
      data-testid="team-progress-drawer"
    >
      <aside
        className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-5 py-4 flex items-center justify-between">
          <div>
            <div className="hw-eyebrow text-[10px] mb-1">Onboarding</div>
            <h2 className="font-display text-lg font-semibold text-[#0F172A]">Team training progress</h2>
            {data && (
              <div className="text-xs text-[#1E3A8A] mt-0.5">
                {data.total_trainings} published training{data.total_trainings === 1 ? "" : "s"} · {data.users.length} staff
              </div>
            )}
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-[#F9F8F6]" data-testid="team-progress-close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          {!data ? (
            <div className="text-center py-12 text-[#1E3A8A] text-sm">Loading…</div>
          ) : data.users.length === 0 ? (
            <div className="text-center py-12 text-[#1E3A8A] text-sm">No active staff yet.</div>
          ) : (
            data.users.map((u) => {
              const pct = u.total ? Math.round((u.completed_count / u.total) * 100) : 0;
              const isOpen = expanded === u.user_id;
              return (
                <div
                  key={u.user_id}
                  data-testid={`progress-row-${u.user_id}`}
                  className={`rounded-xl border ${pct === 100 ? "border-[#86EFAC] bg-[#F0FDF4]" : "border-[#E5E5E0]"}`}
                >
                  <button
                    type="button"
                    onClick={() => setExpanded(isOpen ? null : u.user_id)}
                    className="w-full text-left p-3 flex items-center justify-between gap-3"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[#0F172A] truncate">{u.name || u.email}</span>
                        {u.role && (
                          <span className="text-[10px] uppercase tracking-wider text-[#1E3A8A]">
                            {ROLE_LABELS[u.role] || u.role.replace(/_/g, " ")}
                          </span>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1.5">
                        <div className="flex-1 h-1.5 rounded-full bg-[#F1F5F9] overflow-hidden">
                          <div
                            className={`h-full ${pct === 100 ? "bg-[#166534]" : "bg-[#1E3A8A]"}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        <span className="text-[11px] text-[#0F172A] font-semibold whitespace-nowrap">
                          {u.completed_count} / {u.total}
                        </span>
                      </div>
                    </div>
                  </button>
                  {isOpen && u.missing.length > 0 && (
                    <div className="px-3 pb-3" data-testid={`progress-missing-${u.user_id}`}>
                      <div className="hw-eyebrow text-[10px] mb-1.5">Still to watch</div>
                      <ul className="space-y-1">
                        {u.missing.map((m) => (
                          <li key={m.id} className="text-xs text-[#334155] flex items-center gap-2">
                            <Circle className="w-3 h-3 text-[#1E3A8A]" strokeWidth={2} />
                            <span className="flex-1">{m.title}</span>
                            <span className="text-[10px] text-[#1E3A8A]">{(m.category || "").replace(/_/g, " ")}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {isOpen && u.missing.length === 0 && (
                    <div className="px-3 pb-3 text-xs text-[#166534] font-medium">
                      All caught up! 🎉
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </aside>
    </div>
  );
}

function EmbedModal({ title, embedSrc, url, onClose }) {
  // Lock background scroll while the modal is open
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/70"
      onClick={onClose}
      data-testid="training-embed-modal"
    >
      <div
        className="bg-white rounded-2xl max-w-4xl w-full overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3 border-b border-[#E5E5E0]">
          <h2 className="font-display text-base font-semibold text-[#0F172A] truncate" title={title}>
            {title}
          </h2>
          <div className="flex items-center gap-2">
            {url && (
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                data-testid="training-embed-open-new"
                className="text-xs text-[#1E3A8A] inline-flex items-center gap-1 px-2 py-1 rounded-full hover:bg-[#F9F8F6]"
                title="Open in new tab"
              >
                <ExternalLink className="w-3 h-3" strokeWidth={1.75} /> New tab
              </a>
            )}
            <button
              onClick={onClose}
              data-testid="training-embed-close"
              className="p-1.5 rounded-full hover:bg-[#F9F8F6]"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="relative bg-black" style={{ aspectRatio: "16 / 9" }}>
          {embedSrc ? (
            <iframe
              src={embedSrc}
              title={title}
              className="absolute inset-0 w-full h-full"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
              allowFullScreen
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
              Unable to embed this link. <a href={url} target="_blank" rel="noreferrer" className="underline ml-1">Open in a new tab</a>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function TrainingForm({ existing, onClose, onSaved }) {
  const [form, setForm] = useState({
    title: existing?.title || "",
    description: existing?.description || "",
    kind: existing?.kind || "video",
    url: existing?.url || "",
    category: existing?.category || "getting_started",
    order: existing?.order ?? 0,
    published: existing?.published !== false,
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim()) {
      toast.error("Title is required");
      return;
    }
    setSubmitting(true);
    try {
      if (existing) {
        await api.patch(`/trainings/${existing.id}`, form);
        toast.success("Training updated");
      } else {
        await api.post("/trainings", form);
        toast.success("Training added");
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-[#0F172A]/40">
      <div className="bg-white rounded-2xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E5E0] px-6 py-4 flex justify-between">
          <h2 className="font-display text-xl font-semibold">{existing ? "Edit" : "Add"} Training</h2>
          <button onClick={onClose} data-testid="training-form-close"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4" data-testid="training-form">
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Title *</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              required
              data-testid="training-title"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              placeholder="e.g., Walkthrough: A.N.C.H.O.R. Intake"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              data-testid="training-description"
              className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0]"
              placeholder="A short summary so the team knows what this covers."
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Kind</label>
              <select
                value={form.kind}
                onChange={(e) => setForm({ ...form, kind: e.target.value })}
                data-testid="training-kind"
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              >
                {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
                data-testid="training-category"
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              >
                {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-[#334155] mb-2">Link (YouTube, Loom, Google Doc, PDF URL, etc.)</label>
            <input
              value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })}
              type="url"
              data-testid="training-url"
              className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              placeholder="https://…"
            />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-[#334155] mb-2">Display order</label>
              <input
                value={form.order}
                onChange={(e) => setForm({ ...form, order: Number(e.target.value) || 0 })}
                type="number"
                data-testid="training-order"
                className="w-full px-4 py-2.5 rounded-xl bg-white border border-[#E5E5E0]"
              />
              <div className="text-[11px] text-[#1E3A8A] mt-1">Lower numbers appear first within the category.</div>
            </div>
            <label className="flex items-center gap-2 self-end pb-2 cursor-pointer">
              <input
                type="checkbox"
                checked={form.published}
                onChange={(e) => setForm({ ...form, published: e.target.checked })}
                data-testid="training-published"
                className="rounded"
              />
              <span className="text-sm text-[#334155]">Published (visible to everyone)</span>
            </label>
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-full border border-[#E5E5E0] text-[#334155] font-medium">
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              data-testid="training-submit"
              className="flex-1 py-3 rounded-full bg-[#1E3A8A] text-white font-medium disabled:opacity-60"
            >
              {submitting ? "Saving…" : existing ? "Save changes" : "Add training"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * "New User Quick-Start Guide" download card — pinned at the top of the
 * Training page so brand-new staff can pull a printable onboarding PDF
 * before they dive into individual videos. Open to every authenticated
 * user (volunteers included).
 */
function QuickStartGuideCard() {
  const [downloading, setDownloading] = useState(false);
  const download = async () => {
    setDownloading(true);
    try {
      const token = getStoredToken();
      // Cache-bust the URL so any CDN that may have cached a 404 from before
      // the backend rolled out this route serves the live response instead.
      const url = `${API}/exports/new-user-guide.pdf?v=${Date.now()}`;
      const res = await fetch(url, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        cache: "no-store",
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      if (!blob || blob.size === 0) throw new Error("Empty file");
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = "HarborWell-Connect-New-User-Guide.pdf";
      a.rel = "noopener";
      a.style.display = "none";
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }, 1500);
      toast.success("Quick-Start guide downloaded");
    } catch (e) {
      toast.error(formatApiError(e) || "Download failed");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div
      className="bg-[#E8ECE7] border border-[#1E3A8A]/20 rounded-2xl p-5 md:p-6 flex flex-col md:flex-row gap-4 md:items-center md:justify-between"
      data-testid="quick-start-guide-card"
    >
      <div className="flex items-start gap-4 flex-1">
        <div className="w-12 h-12 rounded-2xl bg-[#1E3A8A] text-white flex items-center justify-center flex-shrink-0">
          <BookOpen className="w-6 h-6" strokeWidth={1.75} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-display text-lg font-semibold text-[#0F172A]">
            New User Quick-Start Guide
          </h2>
          <p className="text-sm text-[#334155] mt-1">
            Printable PDF for brand-new staff — first-time login &amp; Microsoft
            Authenticator setup, an area-by-area tour of the app, and the
            5 most common tasks step-by-step.
          </p>
        </div>
      </div>
      <button
        onClick={download}
        disabled={downloading}
        data-testid="download-new-user-guide-btn"
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#1E3A8A] text-white text-sm font-medium hover:bg-[#172554] disabled:opacity-60 self-start md:self-auto whitespace-nowrap"
      >
        <Download className="w-4 h-4" />
        {downloading ? "Preparing…" : "Download PDF"}
      </button>
    </div>
  );
}

